import React from "react";
import { Formik } from "formik";
import Form from "@poseidon/ui/components/form/FullWidthForm";
import * as api from "./api";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";

import {
  Grid,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { FileManagement } from "../Files";
import { useDealerRiskProfile } from "./hooks/useDealerRiskProfile";
import { useKybNotesLog } from "./hooks/useKybNotesLog";
import { Panel } from "@poseidon/ui/components/Panel";
import {
  FormikDisabler,
  usePermissions,
} from "@poseidon/ui/components";
import { ToggleButtonControl } from "@poseidon/ui/components/ToggleButtonControl";
import NoteLog from "../NoteLog/NoteLog";
import config from "@poseidon/utils/config";

export function DealerRiskProfile({ dealer }) {
  const { canWrite } = usePermissions();
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const dealerId = dealer.id;
  const { dealerRiskProfile, loading, error } = useDealerRiskProfile({
    dealerId,
  });
  const { kybNotesLog } = useKybNotesLog(dealerId);
  const { fileResolvers, yesNoOptions, riskOptions, noteCategories } = config;

  const updateDealerVerifiedStatus = async (values) => {
    try {
      await api.updateDealer(dealerId, values);
      setToast("Successfully updated Verified Status for Dealer");
    } catch (error) {
      setError(error.message);
    }
  };

  const updateDealerRiskProfile = async (values) => {
    try {
      await api.updateDealerRiskProfile({ dealerId, values });
      setToast("Successfully updated Risk Profile for Dealer");
    } catch (error) {
      setError(error.message);
    }
  };

  const onKybNotesAddHandler = (values, { resetForm }) => {
    try {
      api.addNote({ ...values, dealer, noteCategory: noteCategories.kybNote });
      resetForm();
      setToast("Added KYB Note");
    } catch (error) {
      setError(error.message);
    }
  };

  if (error) {
    setError(error);
  }

  if (loading || !dealerRiskProfile) {
    return <CircularProgress sx={{ alignSelf: "center" }} />;
  }

  return (
    <Stack spacing={5} sx={{ px: 5 }}>
      <Typography variant="h5">Dealer Risk Profile</Typography>
      <Panel title="Dealer Verifcation & KYB Status" expandable>
        <Formik
          initialValues={{
            isVerified: dealer.isVerified,
          }}
          enableReinitialize
          onSubmit={updateDealerVerifiedStatus}
        >
          {({ dirty }) => (
            <Form>
              <FormikDisabler disabled={!canWrite} />
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12}>
                  <ToggleButtonControl
                    name="isVerified"
                    label="Is Dealer Verified"
                    options={yesNoOptions}
                  />
                </Grid>

                {dirty && canWrite && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      margin="normal"
                    >
                      Save Status
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Form>
          )}
        </Formik>
        <Formik
          initialValues={{
            kybStatus: dealerRiskProfile.kybStatus,
          }}
          enableReinitialize
          onSubmit={updateDealerRiskProfile}
        >
          {({ dirty }) => (
            <Form>
              <FormikDisabler disabled={!canWrite} />
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12}>
                  <ToggleButtonControl
                    name="kybStatus"
                    label="KYB Status"
                    options={riskOptions}
                  />
                </Grid>

                {dirty && canWrite && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      margin="normal"
                    >
                      Save Status
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Form>
          )}
        </Formik>
      </Panel>

      <Panel
        title="KYB Notes"
        collapsedDetail={`${kybNotesLog.length} notes`}
        expandable
        startCollapsed
      >
        <NoteLog log={kybNotesLog} onSubmitHandler={onKybNotesAddHandler} />
      </Panel>

      <Panel title="File Upload">
        <FileManagement
          fileCategory="dealer"
          resolveMetadata={fileResolvers.dealerRiskProfile}
          dealerId={dealerId}
        />
      </Panel>
    </Stack>
  );
}
